// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (c) 2026 Password Manager contributors
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { SuperadminService } from "@/services/superadmin";
import { OverallStatusBanner } from "@/components/superadmin/system/OverallStatusBanner";
import { ServiceCard } from "@/components/superadmin/system/ServiceCard";
import { ContainerTable } from "@/components/superadmin/system/ContainerTable";
import { AutoRefreshToggle } from "@/components/superadmin/system/AutoRefreshToggle";
import { SystemIssuesList } from "@/components/superadmin/system/SystemIssuesList";
import { Skeleton } from "@/components/ui/skeleton";

export const Route = createFileRoute("/superadmin/system")({
  component: SystemPage,
});

function SystemPage() {
  const { t } = useTranslation("superadmin");
  const [autoRefresh, setAutoRefresh] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["superadmin", "systemHealth"],
    queryFn: () => SuperadminService.getSystemHealth(),
    refetchInterval: autoRefresh ? 30000 : false,
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold">
              {t("systemHealth.title")}
            </h1>
            <p className="text-sm text-muted-foreground">
              {t("systemHealth.description")}
            </p>
          </div>
        </div>
        <Skeleton className="h-14 w-full" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Skeleton className="h-40" />
          <Skeleton className="h-40" />
          <Skeleton className="h-40" />
        </div>
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  const health = data;
  const db = health?.services.database;
  const redis = health?.services.redis;
  const docker = health?.services.docker;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">
            {t("systemHealth.title")}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t("systemHealth.description")}
          </p>
        </div>
        <AutoRefreshToggle
          enabled={autoRefresh}
          onToggle={setAutoRefresh}
          lastUpdated={health?.lastUpdated}
        />
      </div>

      {/* Overall status banner */}
      <OverallStatusBanner status={health?.overallStatus ?? "error"} />

      {/* Service cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <ServiceCard
          title={t("systemHealth.services.database.title")}
          status={db?.status ?? "error"}
          metrics={[
            {
              label: t("systemHealth.services.database.latency"),
              value: `${db?.latency ?? 0}ms`,
            },
            {
              label: t("systemHealth.services.database.connections"),
              value: `${db?.connectionCount ?? 0}/${db?.maxConnections ?? 0}`,
            },
            {
              label: t("systemHealth.services.database.size"),
              value: db?.databaseSize ?? "Unknown",
            },
          ]}
        />
        <ServiceCard
          title={t("systemHealth.services.redis.title")}
          status={redis?.status ?? "error"}
          metrics={[
            {
              label: t("systemHealth.services.database.latency"),
              value: `${redis?.latency ?? 0}ms`,
            },
            {
              label: t("systemHealth.services.redis.memoryUsed"),
              value: `${redis?.memoryUsed ?? "0MB"}/${redis?.memoryMax ?? "Unknown"}`,
            },
            {
              label: t("systemHealth.services.redis.keys"),
              value: redis?.keysCount ?? 0,
            },
            {
              label: t("systemHealth.services.redis.clients"),
              value: redis?.connectedClients ?? 0,
            },
          ]}
        />
        <ServiceCard
          title={t("systemHealth.services.docker.title")}
          status={docker?.status ?? "error"}
          metrics={[
            {
              label: t("systemHealth.services.docker.running"),
              value: `${docker?.containersRunning ?? 0}/${docker?.containersTotal ?? 0}`,
            },
          ]}
        />
      </div>

      {/* System issues */}
      <SystemIssuesList
        issues={health?.issues ?? []}
        issueCount={health?.issueCount ?? { critical: 0, warning: 0, info: 0 }}
      />

      {/* Container table */}
      <ContainerTable containers={docker?.containers ?? []} />
    </div>
  );
}
